import { Component, OnInit, NgZone, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Title } from '@angular/platform-browser';

import { TitleIIIDataEntryService } from './titleIIIDataEntry.service';
import { ClaimService } from '../shared/claim.service';
import { HomeDeliveredMealsUnitsSection } from './HomeDeliveredMealsUnitsSection';
import { CommonService } from '../shared/common.service';
import { ValidationService } from '../shared/validation.service';
import { ToastrService } from '../shared/toastr.service';
import { DataElement } from '../shared/dataElement';
import { TitleIIISectionComponent } from './title-iii-section.component';
import { AddOnOptions } from '../shared/Model/addOnOptions';

@Component({
  templateUrl: 'titleIIIHomeDeliveredMealsUnits.component.html',
})

export class TitleIIIHomeDeliveredMealsUnitsComponent extends TitleIIISectionComponent<HomeDeliveredMealsUnitsSection> implements OnInit, OnDestroy {

  _sectionModel: HomeDeliveredMealsUnitsSection;
  _uploadModels: Array<HomeDeliveredMealsUnitsSection>;
  _batchIds: Array<string>;
  unsavedChanges: boolean = false;

  public get addOnOption(): AddOnOptions {
    return {
      text: '$',
      width: 20,
      isPrefix: true
    };
  }

  constructor(public zone: NgZone,
    public route: ActivatedRoute,
    public router: Router,
    public _titleService: Title,
    public _service: TitleIIIDataEntryService,
    public _claimService: ClaimService,
    public _commonService: CommonService,
    public _validation: ValidationService,
    public toastr: ToastrService
  ) { super(new HomeDeliveredMealsUnitsSection(), _commonService, _validation, router, _claimService, _service);  }

  ngOnInit(): void {
    let title = 'Older Adults - Home Delivered Meals - Service Units, Expenditures and Program Income - Data Submissions - ACL OAAPS';
    this._titleService.setTitle(title);
    this._sectionModel = new HomeDeliveredMealsUnitsSection();
    this.gethomedeliveredmealsunitsbyffyandstate();
  }

  ngOnDestroy(): void {
    super.destroy();
  }

  save(callback: () => void = () => null): void {
    this.unsavedChanges = false;
    // TODO Move this logic into Controller for security
    this._sectionModel.state = this._claimService.currentOrg;
    this._sectionModel.fiscalYear = this._claimService.currentYear;
    super.registerSubscription(this._service.savehomedeliveredmealsunits(this._sectionModel)
      .subscribe(data => this.zone.run(() => {
        this._sectionModel = data;
      }),
      ()=> console.log('error'),
      ()=> {
        //finalize(callback);

        this.toastr.success('Saved!');
        callback();
      }
      ));
  }

  saveAndNext(): void {
    this.unsavedChanges = false;
    const navAction = () => this.router.navigate(['/data-submissions/titleIIIAdultDayCareChars']);
    if (!this._claimService.isViewOnly && 
      this._commonService.shouldSave(this._sectionModel, this._claimService.isAAAEnabledStateUser(), this._service.aaaStateEditMode)) {
      this.save(navAction);
    } else {
      navAction();
    }
  }

  next(): void {
    this.router.navigate(['/data-submissions/titleIIIAdultDayCareChars']);
}

  gethomedeliveredmealsunitsbyffyandstate(): void {
    super.registerSubscription(this._service.get<HomeDeliveredMealsUnitsSection[]>(this._claimService,
      this._service._gethomedeliveredmealsunitsbyffyandstate, this.resolveSectionName())
      .subscribe(data => this.zone.run(() => {
        if (data.length > 0) {
          // Adding this so if the filter returns nothing, _sectionModel won't be undefined;
          let tempSectionModel: HomeDeliveredMealsUnitsSection = new HomeDeliveredMealsUnitsSection();
          tempSectionModel = data.filter(element => element.batchId === '00000000-0000-0000-0000-000000000000')[0];
          if (tempSectionModel) {
            this._sectionModel = tempSectionModel;
          }
          this._uploadModels = data.filter(element => element.batchId !== '00000000-0000-0000-0000-000000000000');
          if (this._uploadModels != null && this._uploadModels.length > 0) {
            this._batchIds = this._uploadModels.map<string>(e => e.batchId);
          }
          this.formatAllNumbersAlreadySaved();
          super.processSectionStatus();
        }

      })));
  }

  checkUserResponsibility() {
    let calculateTotals = false;
    if (this._sectionModel.totalServiceUnits.isRequired &&
      this.checkUserExpenditureResponsibility()) {
      calculateTotals = true; // User is responsible for everything
    } else {
      this._sectionModel.totalExpendPerUnit.elementValue = 'N/A';
    }
    return calculateTotals;
  }

  checkUserExpenditureResponsibility() {
    return this._sectionModel.expendA.isRequired &&
      this._sectionModel.expendC.isRequired &&
      this._sectionModel.expendOtherState.isRequired &&
      this._sectionModel.expendOtherNonState.isRequired &&
      this._sectionModel.programIncome.isRequired;
  }

  updateTotals(): void {
    let grandTotal = 0;
    let perUnit = 0.00;
    let totalServiceUnits = 0;
    if (this.checkUserExpenditureResponsibility()) {
      const expendC = parseFloat(this.removeCommas(this._sectionModel.expendC.elementValue));
      const expendA = parseFloat(this.removeCommas(this._sectionModel.expendA.elementValue));
      const expendOtherState = parseFloat(this.removeCommas(this._sectionModel.expendOtherState.elementValue));
      const expendOtherNonState = parseFloat(this.removeCommas(this._sectionModel.expendOtherNonState.elementValue));
      const programIncome = parseFloat(this.removeCommas(this._sectionModel.programIncome.elementValue));
      totalServiceUnits = parseFloat(this.removeCommas(this._sectionModel.totalServiceUnits.elementValue));
      if (!isNaN(expendC)) { grandTotal += expendC; }
      if (!isNaN(expendA)) { grandTotal += expendA; }
      if (!isNaN(expendOtherState)) { grandTotal += expendOtherState; }
      if (!isNaN(expendOtherNonState)) { grandTotal += expendOtherNonState; }
      if (!isNaN(programIncome)) { grandTotal += programIncome; }
      this._sectionModel.expendTotal.elementValue = this.addDecimals(grandTotal.toString());
    } else {
      this._sectionModel.expendTotal.elementValue = 'N/A';
    }
    if (this.checkUserResponsibility()) {
      this._sectionModel.totalExpendPerUnit.elementValue = this.expendPerUnit(grandTotal, totalServiceUnits);
    }
  }

  formatAllNumbersAlreadySaved(): void {
    this._sectionModel.totalServiceUnits.elementValue = this.addComma(this._sectionModel.totalServiceUnits.elementValue);
    this._sectionModel.expendC.elementValue = this.addDecimals(this._sectionModel.expendC.elementValue);
    this._sectionModel.expendA.elementValue = this.addDecimals(this._sectionModel.expendA.elementValue);
    this._sectionModel.expendOtherState.elementValue = this.addDecimals(this._sectionModel.expendOtherState.elementValue);
    this._sectionModel.expendOtherNonState.elementValue = this.addDecimals(this._sectionModel.expendOtherNonState.elementValue);
    this._sectionModel.programIncome.elementValue = this.addDecimals(this._sectionModel.programIncome.elementValue);
    this._sectionModel.expendTotal.elementValue = this.addDecimals(this._sectionModel.expendTotal.elementValue);
    this._sectionModel.totalExpendPerUnit.elementValue = this.addDecimals(this._sectionModel.totalExpendPerUnit.elementValue);
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.unsavedChanges || sessionStorage.getItem('sessionExpiredRedirect')) {
      this._commonService.oaLockedNav = false;
      return true;
    } else {
      this._commonService.oaLockedNav = true;
      return false;
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  showMsg($event) {
    if (!this.canDeactivate()) {
      $event.returnValue = `You have unsaved changes. Press OK to proceed without saving these changes,
or press Cancel to go back and save these changes.`;
    }
  }

}
